import React from "react"
import { useTranslation } from "react-i18next"
import CircleTickIcon from "@/assets/icons/task/circle-tick-icon.tsx"
import { AnimalFilterTab } from "@/components/filter-tab/animal-filter-tab/animal-filter-tab.tsx"
import NoResultsFound from "@/components/not-found/no-results-found.tsx"
import PendingCircle from "@/components/pending-circle/pending-circle.tsx"
import styles from "@/components/shadcn/drawer/custom-drawer.module.css"
import DrawerTopBar from "@/components/shadcn/drawer/drawer-top-bar.tsx"
import { DrawerClose } from "@/components/shadcn/drawer.tsx"
import AnimalCard from "@/features/farm/components/animal-card/animal-card.tsx"
import useAnimalFilterURL from "@/features/farm/hooks/use-animal-filter-URL.ts"
import { BROWN } from "@/utils/constants/colors-constants.ts"

interface WrapperProps {
  children: React.ReactNode
  selectionType: "single" | "multiple"
}

const SelectionWrapper: React.FC<WrapperProps> = ({
  children,
  selectionType,
}) => {
  if (selectionType === "single") {
    return <DrawerClose>{children}</DrawerClose>
  }
  return <div>{children}</div>
}

interface DrawerContentProps {
  currentValue: string[] | string
  onSaveClick: (animalIDs: string[]) => void
  isNested?: boolean
  placeholder: string
  selectionType?: "single" | "multiple"
}

const DrawerAnimalContent: React.FC<DrawerContentProps> = ({
  currentValue,
  onSaveClick,
  placeholder,
  selectionType = "multiple",
}) => {
  const { t } = useTranslation()

  const {
    selections,
    animals,
    handleAgeSelection,
    handleGenderSelection,
    handleSpeciesSelection,
    handleSearchQuery,
    handleStatusSelection,
    handleOutOfExploitationSelection,
  } = useAnimalFilterURL()

  const isSelected = (animalId: string) => {
    return currentValue.includes(animalId)
  }

  const handleAnimalSelection = (animalId: string) => {
    if (isSelected(animalId) && selectionType === "multiple") {
      if (typeof currentValue !== "string") {
        const newArray = currentValue.filter((id) => id !== animalId)
        onSaveClick(newArray)
      }
    } else if (isSelected(animalId) && selectionType === "single") {
      onSaveClick([])
    } else if (selectionType === "multiple") {
      onSaveClick([...currentValue, animalId])
    } else {
      onSaveClick([animalId])
    }
  }

  const sortedAnimals = [
    ...animals
      .filter((animal) => isSelected(animal.id))
      .sort((a, b) => a.name.localeCompare(b.name)),
    ...animals
      .filter((animal) => !isSelected(animal.id))
      .sort((a, b) => a.name.localeCompare(b.name)),
  ]

  return (
    <>
      <DrawerTopBar
        searchQuery={selections.searchQuery}
        setSearchQuery={handleSearchQuery}
        placeholder={placeholder}
      />
      <div className={styles["filter-tab"]}>
        <AnimalFilterTab
          selections={selections}
          handleAgeChange={handleAgeSelection}
          handleGenderChange={handleGenderSelection}
          handleSpeciesChange={handleSpeciesSelection}
          handleStatusChange={handleStatusSelection}
          handleOutOfExploitationChange={handleOutOfExploitationSelection}
        />
      </div>
      <div className={styles["slide-up-content-wrapper"]}>
        <div className={styles["slide-up-content"]}>
          {!animals ||
            (animals.length === 0 && (
              <NoResultsFound
                className={"mt-20"}
                message={t("noAnimalsFound")}
              />
            ))}
          {sortedAnimals.map((animal) => (
            <SelectionWrapper selectionType={selectionType} key={animal.id}>
              <div
                className={
                  isSelected(animal.id)
                    ? styles["animal-card-selected"]
                    : styles["animal-card"]
                }
                onClick={() => handleAnimalSelection(animal.id)}
              >
                {isSelected(animal.id) ? (
                  <CircleTickIcon fill={BROWN} size={24} />
                ) : (
                  <PendingCircle />
                )}
                <AnimalCard animal={animal} />
              </div>
              <div className={styles["separator"]}></div>
            </SelectionWrapper>
          ))}
        </div>
      </div>
    </>
  )
}

export default DrawerAnimalContent
